import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCachedResource } from "@/lib/dataCache";
import { useAuth } from "@/context/AuthContext";
import {
  Package, TrendingUp, BarChart3, Users, Activity, ArrowRight,
  LineChart as LineChartIcon, Receipt, Wallet, Layers,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, LineChart, Bar, Line,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from "recharts";

// DRIVN admin dashboard. No subcontractors — money is customer-side (sell) vs
// carrier cost (buy_rate); margin = sell − buy. Brand teal #19e6c8 on #070e23.
const STATUS_LABEL = {
  needs_approval: "Needs Approval",
  assigned: "Assigned", pending: "Assigned",
  onboard: "Onboard", done: "Delivered", futile: "Futile",
};
const STATUS_COLOR = {
  needs_approval: "#f59e0b", assigned: "#19e6c8", pending: "#19e6c8",
  onboard: "#a78bfa", done: "#22c55e", futile: "#ef4444",
};

// Distinct line colours for the client-comparison chart.
const CLIENT_PALETTE = [
  "#19e6c8", "#a78bfa", "#22c55e", "#f59e0b", "#ec4899",
  "#3b82f6", "#f97316", "#06b6d4", "#e879f9", "#84cc16",
];

const fmtCurrency = (n) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function fmtCompact(n) {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dash = useCachedResource("dashboard-summary", () =>
    api.get("/admin/dashboard-summary").then((r) => r.data));
  const data = dash.data ?? null;
  const loading = dash.loading;
  const err = dash.error ? (dash.error.response?.data?.detail || dash.error.message) : "";

  const stats = useMemo(() => ([
    { label: "Total Loads", value: data?.total_loads ?? 0, icon: Package, color: "#19e6c8",
      sub: data ? `${data.monthly_loads || 0} this month` : "" },
    { label: "Monthly Margin", value: fmtCurrency(data?.monthly_margin ?? 0), icon: TrendingUp, color: "#22c55e",
      sub: data ? `${data.monthly_loads || 0} jobs this month` : "" },
    { label: "Average Margin", value: fmtCurrency(data?.avg_margin ?? 0), icon: BarChart3, color: "#a78bfa",
      sub: data ? `Across ${data.total_loads || 0} loads` : "" },
    { label: "Customers", value: data?.total_clients ?? 0, icon: Users, color: "#06b6d4",
      sub: data ? `${data.total_loads || 0} loads total` : "" },
    { label: "Total Sales", value: fmtCurrency(data?.billed_total ?? 0), icon: Receipt, color: "#ec4899",
      sub: "Across all loads (inc GST)" },
    { label: "Outstanding", value: fmtCurrency(data?.outstanding_total ?? 0), icon: Wallet, color: "#f59e0b",
      sub: data ? `${fmtCurrency(data.received_total)} received` : "" },
  ]), [data]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard/job-status" replace />;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-white/50 text-sm">
        Loading dashboard…
      </div>
    );
  }
  if (err) {
    return (
      <div className="p-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200">
        {String(err)}
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="brand-title text-2xl md:text-3xl">Dashboard</h1>
          <p className="text-white/55 text-sm mt-1">
            Operational snapshot — loads, margins, customers and recent activity.
          </p>
        </div>
        <button
          onClick={() => navigate("/dashboard/create-job")}
          className="px-4 py-2 rounded-xl text-[#04201c] font-display tracking-widest text-xs font-bold"
          style={{ background: "linear-gradient(90deg, #19e6c8, #0fbfa6)" }}
          data-testid="dashboard-create-cta"
        >
          + Create Job
        </button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.label}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-col gap-2"
              data-testid={`kpi-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-white/50">{s.label}</span>
                <span className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: `${s.color}22`, color: s.color }}>
                  <Icon size={15} />
                </span>
              </div>
              <div className="text-2xl font-bold text-white leading-none mt-1">{s.value}</div>
              {s.sub && <div className="text-[11px] text-white/40">{s.sub}</div>}
            </div>
          );
        })}
      </div>

      {/* Client comparison — jobs booked vs paid vs billed, selectable clients + granularity */}
      <ClientComparisonCard />

      {/* Profit by month */}
      <MonthlyProfitCard />

      {/* Profit / Loss chart */}
      <ProfitLossCard />

      {/* By customer + recent loads */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="brand-title text-base">Loads by Customer</h3>
            <span className="text-[10px] uppercase tracking-widest text-white/40">
              {data?.by_client?.length || 0} customers
            </span>
          </div>
          {(!data?.by_client || data.by_client.length === 0) ? (
            <p className="text-white/40 text-sm">No customer activity yet.</p>
          ) : (
            <div className="space-y-2.5">
              {data.by_client.slice(0, 10).map((c) => {
                const pct = data.total_loads > 0 ? Math.round((c.load_count / data.total_loads) * 100) : 0;
                return (
                  <div key={c.id} data-testid={`client-row-${c.id}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-white/85 truncate pr-2">{c.name}</span>
                      <span className="text-white/60 font-mono text-xs whitespace-nowrap">
                        {c.load_count} loads
                        <span className={`ml-2 ${c.margin >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                          {c.margin < 0 ? "−" : ""}{fmtCurrency(Math.abs(c.margin))}
                        </span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                      <div className="h-full rounded-full"
                        style={{ width: `${pct}%`, background: "linear-gradient(90deg, #a78bfa, #19e6c8)" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="brand-title text-base flex items-center gap-2">
              <Activity size={15} className="text-[#19e6c8]" /> Recent Loads
            </h3>
            <button onClick={() => navigate("/dashboard/job-status")}
              className="text-xs uppercase tracking-widest text-white/55 hover:text-white flex items-center gap-1">
              View all <ArrowRight size={12} />
            </button>
          </div>
          {(!data?.recent_loads || data.recent_loads.length === 0) ? (
            <p className="text-white/40 text-sm">No loads yet — create your first job.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#19e6c8] text-left text-[10px] uppercase tracking-wider">
                    <th className="py-2 pr-3 font-semibold">Job</th>
                    <th className="py-2 pr-3 font-semibold">Customer</th>
                    <th className="py-2 pr-3 font-semibold">Invoice</th>
                    <th className="py-2 pr-3 font-semibold text-right">Margin</th>
                    <th className="py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_loads.map((j) => (
                    <tr key={j.id} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer"
                      onClick={() => navigate("/dashboard/job-status")} data-testid={`recent-${j.job_ref}`}>
                      <td className="py-2.5 pr-3">
                        <div className="text-white font-semibold font-mono" style={{ color: "#19e6c8" }}>
                          {j.carrier_ref || j.job_ref}
                        </div>
                        {j.carrier_ref && j.job_ref && (
                          <div className="text-[10px] mt-0.5 text-white/55">
                            Ref: <span className="font-mono text-white/75">{j.job_ref}</span>
                          </div>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-white/80 max-w-[140px] truncate" title={j.customer}>{j.customer}</td>
                      <td className="py-2.5 pr-3 font-mono text-xs text-white/60">{j.invoice_number || "—"}</td>
                      <td className={`py-2.5 pr-3 text-right font-mono whitespace-nowrap ${j.margin == null ? "text-white/35" : (j.margin >= 0 ? "text-emerald-300" : "text-red-300")}`}>
                        {j.margin == null ? "—" : `${j.margin < 0 ? "−" : ""}${fmtCurrency(Math.abs(j.margin))}`}
                      </td>
                      <td className="py-2.5">
                        <span className="text-[10px] font-semibold px-2 py-1 rounded-full"
                          style={{ background: `${STATUS_COLOR[j.status] || "#9ca3af"}22`, color: STATUS_COLOR[j.status] || "#9ca3af" }}>
                          {STATUS_LABEL[j.status] || j.status || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Client comparison ──────────────────────────────────────────────────────
const COMP_GRANULARITY = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "yearly", label: "Yearly" },
];
const COMP_METRIC = [
  { key: "jobs", label: "Jobs Booked", currency: false },
  { key: "paid", label: "Amount Paid", currency: true },
  { key: "billed", label: "Billed", currency: true },
];

function ClientComparisonCard() {
  const [gran, setGran] = useState("monthly");
  const [metric, setMetric] = useState("jobs");
  const [resp, setResp] = useState(null);
  const [selected, setSelected] = useState([]);   // client ids
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const metricMeta = COMP_METRIC.find((m) => m.key === metric);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    (async () => {
      try {
        const { data } = await api.get("/admin/client-comparison", { params: { granularity: gran } });
        if (cancelled) return;
        setResp(data);
        // Default-select the top 5 clients by billed (or keep prior selection that still exists).
        setSelected((prev) => {
          const ids = (data.clients || []).map((c) => c.id);
          const kept = prev.filter((id) => ids.includes(id));
          if (kept.length) return kept;
          return ids.slice(0, 5);
        });
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gran]);

  const clients = resp?.clients || [];
  const nameOf = (id) => clients.find((c) => c.id === id)?.name || id;

  const chartData = useMemo(() => {
    if (!resp) return [];
    return (resp.buckets || []).map((b) => {
      const row = { label: b.label };
      selected.forEach((cid) => {
        row[cid] = resp.data?.[cid]?.[b.key]?.[metric] ?? 0;
      });
      return row;
    });
  }, [resp, selected, metric]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const fmtVal = (v) => (metricMeta.currency ? fmtCurrency(v) : `${v}`);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="client-comparison-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="brand-title text-base flex items-center gap-2">
            <Layers size={15} className="text-[#19e6c8]" /> Client Comparison
          </h3>
          <p className="text-[11px] text-white/45 mt-0.5">
            How many jobs each customer booked and how much they paid — pick clients, metric & period.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
            {COMP_METRIC.map((m) => (
              <button key={m.key} type="button" onClick={() => setMetric(m.key)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                  metric === m.key ? "bg-[#19e6c8]/20 text-[#19e6c8]" : "bg-white/[0.03] text-white/55 hover:bg-white/10 hover:text-white"}`}
                data-testid={`comp-metric-${m.key}`}>
                {m.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
            {COMP_GRANULARITY.map((g) => (
              <button key={g.key} type="button" onClick={() => setGran(g.key)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                  gran === g.key ? "bg-[#a78bfa]/20 text-[#c4b5fd]" : "bg-white/[0.03] text-white/55 hover:bg-white/10 hover:text-white"}`}
                data-testid={`comp-gran-${g.key}`}>
                {g.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Client selector chips */}
      {clients.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {clients.slice(0, 24).map((c) => {
            const on = selected.includes(c.id);
            const color = CLIENT_PALETTE[selected.indexOf(c.id) % CLIENT_PALETTE.length] || "#94a3b8";
            return (
              <button key={c.id} type="button" onClick={() => toggle(c.id)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                  on ? "text-white" : "text-white/50 border-white/10 hover:text-white/80"}`}
                style={on ? { borderColor: color + "88", background: color + "1f" } : {}}
                data-testid={`comp-client-${c.id}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: on ? color : "#475569" }} />
                {c.name}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ height: 320 }}>
        {loading && <div className="h-full flex items-center justify-center text-white/40 text-sm">Loading…</div>}
        {!loading && err && <div className="h-full flex items-center justify-center text-red-300 text-sm">{String(err)}</div>}
        {!loading && !err && (chartData.length === 0 || selected.length === 0) && (
          <div className="h-full flex items-center justify-center text-white/40 text-sm">
            {selected.length === 0 ? "Select at least one client above." : "No data in this period yet."}
          </div>
        )}
        {!loading && !err && chartData.length > 0 && selected.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 12, bottom: 0, left: -4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }} tickLine={false}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }} tickLine={false}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                tickFormatter={(v) => (metricMeta.currency ? fmtCompact(v) : v)} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#0b1430", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#fff", fontWeight: 600 }}
                formatter={(value, key) => [fmtVal(value), nameOf(key)]} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 4 }} formatter={(key) => nameOf(key)} />
              {selected.map((cid, i) => (
                <Line key={cid} type="monotone" dataKey={cid} name={cid}
                  stroke={CLIENT_PALETTE[i % CLIENT_PALETTE.length]} strokeWidth={2.2}
                  dot={{ r: 2.5 }} activeDot={{ r: 5 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ── Profit by month ─────────────────────────────────────────────────────────
function MonthlyProfitCard() {
  const currentMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const monthOptions = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 36 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
      return { value, label };
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    (async () => {
      try {
        const { data } = await api.get("/admin/monthly-profit", { params: { month } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [month]);

  const margin = data?.margin ?? 0;
  const marginColor = margin >= 0 ? "#22c55e" : "#ef4444";
  const isCurrent = month === currentMonth;
  const monthLabel = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    if (!y || !m) return month;
    return new Date(y, m - 1, 1).toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  }, [month]);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="monthly-profit-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="brand-title text-base flex items-center gap-2">
            <TrendingUp size={15} className="text-emerald-300" /> Profit by Month
          </h3>
          <p className="text-[11px] text-white/45 mt-0.5">
            {isCurrent ? "This month so far" : monthLabel} · sell − buy across jobs dated that month.
          </p>
        </div>
        <select value={month} onChange={(e) => setMonth(e.target.value || currentMonth)}
          className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white/85 focus:outline-none focus:border-emerald-400/50 cursor-pointer"
          data-testid="monthly-profit-month">
          {monthOptions.map((o) => <option key={o.value} value={o.value} className="bg-[#0b1430]">{o.label}</option>)}
        </select>
      </div>
      {loading && <div className="py-8 text-center text-white/40 text-sm">Loading…</div>}
      {!loading && err && <div className="py-8 text-center text-red-300 text-sm">{String(err)}</div>}
      {!loading && !err && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Profit" value={fmtCurrency(margin)} color={marginColor} />
          <MiniStat label="Sell" value={fmtCurrency(data?.sell)} color="#19e6c8" />
          <MiniStat label="Buy" value={fmtCurrency(data?.buy)} color="#f59e0b" />
          <MiniStat label="Loads" value={data?.loads ?? 0} color="#06b6d4" />
        </div>
      )}
    </div>
  );
}

// ── Profit / Loss ─────────────────────────────────────────────────────────
const PNL_RANGES = [
  { key: "1d", label: "1D" }, { key: "1w", label: "1W" }, { key: "1m", label: "1M" },
  { key: "3m", label: "3M" }, { key: "6m", label: "6M" }, { key: "1y", label: "1Y" },
];

function ProfitLossCard() {
  const [range, setRange] = useState("1m");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    (async () => {
      try {
        const { data } = await api.get("/admin/profit-loss", { params: { range } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range]);

  const totals = data?.totals;
  const margin = totals?.margin ?? 0;
  const marginColor = margin >= 0 ? "#22c55e" : "#ef4444";

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="pnl-chart-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="brand-title text-base flex items-center gap-2">
            <LineChartIcon size={15} className="text-emerald-300" /> Profit &amp; Loss
          </h3>
          <p className="text-[11px] text-white/45 mt-0.5">Sell vs Buy and margin trend by job date — toggle the window.</p>
        </div>
        <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
          {PNL_RANGES.map((r) => (
            <button key={r.key} type="button" onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                range === r.key ? "bg-emerald-500/20 text-emerald-200" : "bg-white/[0.03] text-white/55 hover:bg-white/10 hover:text-white"}`}
              data-testid={`pnl-range-${r.key}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MiniStat label="Sell" value={fmtCurrency(totals.sell)} color="#19e6c8" />
          <MiniStat label="Buy" value={fmtCurrency(totals.buy)} color="#f59e0b" />
          <MiniStat label="Margin" value={fmtCurrency(margin)} color={marginColor} />
          <MiniStat label="Loads" value={totals.load_count ?? 0} color="#06b6d4" />
        </div>
      )}

      <div style={{ height: 320 }}>
        {loading && <div className="h-full flex items-center justify-center text-white/40 text-sm">Loading chart…</div>}
        {!loading && err && <div className="h-full flex items-center justify-center text-red-300 text-sm">{String(err)}</div>}
        {!loading && !err && data?.points?.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.points} margin={{ top: 10, right: 12, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }} tickLine={false}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }} tickLine={false}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickFormatter={(v) => fmtCompact(v)} />
              <Tooltip contentStyle={{ background: "#0b1430", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#fff", fontWeight: 600 }} formatter={(value, name) => [fmtCurrency(value), name]} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
              <Bar dataKey="sell" name="Sell" fill="#19e6c8" radius={[4, 4, 0, 0]} barSize={18} />
              <Bar dataKey="buy" name="Buy" fill="#f59e0b" radius={[4, 4, 0, 0]} barSize={18} />
              <Line type="monotone" dataKey="margin" name="Margin" stroke="#22c55e" strokeWidth={2.5}
                dot={{ r: 3, fill: "#22c55e" }} activeDot={{ r: 5 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {!loading && !err && (!data?.points || data.points.length === 0) && (
          <div className="h-full flex items-center justify-center text-white/40 text-sm">No loads in this window yet.</div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="rounded-lg px-3 py-2 border" style={{ background: `${color}0f`, borderColor: `${color}33` }}>
      <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>{label}</div>
      <div className="text-base font-bold mt-0.5 text-white">{value}</div>
    </div>
  );
}
